'use client'

import Image from 'next/image'
import { useTranslations } from 'next-intl'

import type { ActivationSummary, ProgressStep } from '@/features/clubs/types'
import Button from '@/shared/ui/Button'
import SoonBadge from '@/shared/ui/SoonBadge'

import S from './ActivationProgressPanel.module.scss'

interface ActivationProgressPanelProps {
  steps: ProgressStep[]
  /**
   * Current fan count for the "recruit fans" step counter (step 3).
   * Comes from club.fan_count — backend ProgressStep doesn't carry progress
   * numbers yet, so the current value is piped in from the page level.
   */
  fanCount?: number
  /**
   * BAT-1101/1108: «X / threshold» from the owner profile — the counted fans
   * (an athlete's leave out linked accounts) and the admin threshold. Wins
   * over fanCount / 100 when present.
   */
  summary?: ActivationSummary
  /** BAT-1108: the athlete's steps — approved, announcement, fans. */
  isAthlete?: boolean
}

type NormalisedStatus = 'completed' | 'in_progress' | 'pending'

// Back-end гарантирует порядок шагов (см. vault/arena/wiki/services/clubs.md:117):
// 0 — kyc_approved, 1 — social_proof_submitted, 2 — fan_count_minimum.
// Заголовки, описания, счётчик и CTA привязаны к этому порядку, а не к step.name.
const STEP_TITLE_KEYS = [
  'stepTitles.registration',
  'stepTitles.socialPost',
  'stepTitles.recruitFans'
] as const

const STEP_DESCRIPTION_KEYS = [
  'steps.registration',
  'steps.socialPost',
  'steps.recruitFans'
] as const

// BAT-1101: an athlete's steps come as athlete_approved (BAT-597, was
// athlete_verified), announcement_confirmed, fan_count_minimum — the same
// order as the club's.
const ATHLETE_STEP_TITLE_KEYS = [
  'athlete.stepTitles.approved',
  'athlete.stepTitles.announcement',
  'athlete.stepTitles.recruitFans'
] as const

const ATHLETE_STEP_DESCRIPTION_KEYS = [
  'athlete.steps.approved',
  'athlete.steps.announcement',
  'athlete.steps.recruitFans'
] as const

const ANNOUNCEMENT_STEP_INDEX = 1
const FAN_COUNT_STEP_INDEX = 2
const DEFAULT_fanTarget = 100

function normaliseStatus(status: string): NormalisedStatus {
  if (status === 'completed') return 'completed'
  if (status === 'in_progress') return 'in_progress'
  return 'pending'
}

const STATUS_TO_CLASS: Record<NormalisedStatus, string> = {
  completed: S.statusCompleted,
  in_progress: S.statusCurrent,
  pending: S.statusFuture
}

const ActivationProgressPanel = ({
  steps,
  fanCount = 0,
  summary,
  isAthlete = false
}: ActivationProgressPanelProps) => {
  const t = useTranslations('club.activation')

  if (steps.length === 0) return null

  const fanTarget = summary?.fans_threshold ?? DEFAULT_fanTarget
  const countedFans = summary ? summary.fans_counted : fanCount
  const clampedFanCount = Math.max(
    0,
    Number.isFinite(countedFans) ? countedFans : 0
  )
  const titleKeys = isAthlete ? ATHLETE_STEP_TITLE_KEYS : STEP_TITLE_KEYS
  const descriptionKeys = isAthlete
    ? ATHLETE_STEP_DESCRIPTION_KEYS
    : STEP_DESCRIPTION_KEYS
  // US-ATH-09.1: the fans are in, the announcement is not — say why the
  // withdrawal is still closed.
  const needsAnnouncement =
    isAthlete &&
    steps[FAN_COUNT_STEP_INDEX]?.status === 'completed' &&
    steps[ANNOUNCEMENT_STEP_INDEX]?.status !== 'completed'
  const fanProgressPct = Math.min(100, (clampedFanCount / fanTarget) * 100)

  return (
    <section className={S.panel}>
      <header className={S.header}>
        <div className={S.headerText}>
          <div className={S.headerCol}>
            <h2 className={S.title}>
              {isAthlete ? t('athlete.title') : t('title')}
            </h2>
          </div>
          <div className={S.headerCol}>
            <p className={S.description}>
              {isAthlete ? t('athlete.description') : t('description')}
            </p>
          </div>
        </div>
      </header>

      <ol className={S.steps}>
        {steps.map((step, idx) => {
          const normalised = normaliseStatus(step.status)
          const titleKey = titleKeys[idx]
          const descriptionKey = descriptionKeys[idx]
          const isFanCountStep = idx === FAN_COUNT_STEP_INDEX
          // Use localized title for known steps (0..2 from ADR-007 order);
          // fall back to raw step.name if backend ever returns an extra step.
          // The fans step names the admin threshold (BAT-1101), not a fixed 100.
          const displayName = !titleKey
            ? step.name
            : isFanCountStep
            ? t(
                isAthlete
                  ? 'athlete.stepTitles.recruitFans'
                  : 'stepTitles.recruitFansCount',
                {
                  count: fanTarget
                }
              )
            : t(titleKey)
          return (
            <li key={idx} className={S.stepGroup}>
              <div className={`${S.step} ${STATUS_TO_CLASS[normalised]}`}>
                {normalised === 'completed' && (
                  <span className={S.statusLabel}>{t('step.completed')}</span>
                )}
                {idx === 0 && normalised === 'completed' && (
                  <Image
                    src="/assets/club-profile/club-profile-step-1.png"
                    alt=""
                    width={98}
                    height={130}
                    className={S.stepNumberImage}
                    priority
                  />
                )}
                {idx === 1 && (
                  <Image
                    src="/assets/club-profile/club-profile-step-2.png"
                    alt=""
                    width={98}
                    height={130}
                    className={`${S.stepNumberImage} ${S.stepNumberImageRight30}`}
                    priority
                  />
                )}
                {idx === 2 && (
                  <Image
                    src="/assets/club-profile/club-profile-step-3.png"
                    alt=""
                    width={98}
                    height={130}
                    className={`${S.stepNumberImage} ${S.stepNumberImageRight30}`}
                    priority
                  />
                )}
                <div className={S.stepBody}>
                  <div className={S.stepHead}>
                    <span className={S.stepName}>{displayName}</span>
                    {isFanCountStep && normalised !== 'completed' && (
                      <span className={S.stepCounter}>
                        {t('counter.fans', {
                          current: Math.min(clampedFanCount, fanTarget),
                          target: fanTarget
                        })}
                      </span>
                    )}
                  </div>
                  {descriptionKey && (
                    <p className={S.stepDescription}>{t(descriptionKey)}</p>
                  )}
                </div>

                {isFanCountStep && normalised !== 'completed' && (
                  <div
                    className={S.fanProgressTrack}
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={fanTarget}
                    aria-valuenow={Math.min(clampedFanCount, fanTarget)}
                    aria-label={t('counter.fans', {
                      current: Math.min(clampedFanCount, fanTarget),
                      target: fanTarget
                    })}
                  >
                    <div
                      className={S.fanProgressFill}
                      style={{ width: `${fanProgressPct}%` }}
                    />
                  </div>
                )}
              </div>
              {/* An athlete's post is its partnership announcement: the CTA
                  sits on that step only (a club keeps it on the current one). */}
              {normalised === 'in_progress' &&
                (!isAthlete || idx === ANNOUNCEMENT_STEP_INDEX) && (
                  // TODO(OCE-629): wire up CTA handler once backend contract
                  // exposes per-step action URL / dialog binding. See Figma
                  // node 3812-16724. Until then the button is disabled so it
                  // can't trick users into clicking a no-op (review feedback,
                  // see OCE-987).
                  <span className={S.ctaWrap}>
                    <SoonBadge label={t('soonBadge')} className={S.soonBadge} />
                    <Button
                      type="button"
                      color="secondary"
                      disabled
                      className={`${S.cta} ${S.ctaInline}`}
                      fullWidth
                      aria-label={`${t('cta.launchPost')} (${t('soonBadge')})`}
                    >
                      {t('cta.launchPost')}
                    </Button>
                  </span>
                )}
            </li>
          )
        })}
      </ol>

      {needsAnnouncement ? (
        <p className={S.notice} role="status">
          {t('athlete.announcementNeeded', { count: fanTarget })}
        </p>
      ) : null}
    </section>
  )
}

export default ActivationProgressPanel
